import React, { useMemo } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { NotifyTargetOutDTO, NotifyTargetTypeEnum } from '@api/ringil-api.v2.tsx';
import RingilDropdown, { RingilDropdownItem } from '@components/RingilComponents/RingilDropdown';

import SvgGroup from '@icons/grid-horizontal-inverted.svg?react';
import SvgUser from '@icons/user-inverted.svg?react';

interface NotifyDropdownProperties {
  notifyTargetOptions: NotifyTargetOutDTO[];
  values?: NotifyTargetOutDTO[];
  onSelect: (values: NotifyTargetOutDTO[]) => void;
  readonly?: boolean;
  multiSelect?: boolean;
  selectAllEnabled?: boolean;
  dataTestIdBase?: string;
}

const composeId = (target: { id: string; notifyTargetType: NotifyTargetTypeEnum }) =>
  `${target.notifyTargetType}::${target.id}`;

const getIconByType = (type: NotifyTargetTypeEnum) => {
  switch (type) {
    case NotifyTargetTypeEnum.USER:
      return <SvgUser className="w-4 h-4" />;
    case NotifyTargetTypeEnum.DEPARTMENT:
      return <SvgGroup className="w-4 h-4" />;
    default:
      return null;
  }
};

const NotifyDropdown: React.FC<NotifyDropdownProperties> = ({
  notifyTargetOptions,
  values = [],
  onSelect,
  readonly = false,
  multiSelect = true,
  selectAllEnabled = true,
  dataTestIdBase,
}) => {
  const { tr } = useTranslationLgs();

  const orderedTargets = useMemo(() => {
    const departments = notifyTargetOptions
      .filter(item => item.notifyTargetType === NotifyTargetTypeEnum.DEPARTMENT)
      .sort((a, b) => a.name.localeCompare(b.name));
    const users = notifyTargetOptions
      .filter(item => item.notifyTargetType === NotifyTargetTypeEnum.USER)
      .sort((a, b) => a.name.localeCompare(b.name));
    const others = notifyTargetOptions
      .filter(item => item.notifyTargetType !== NotifyTargetTypeEnum.DEPARTMENT && item.notifyTargetType !== NotifyTargetTypeEnum.USER)
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...departments, ...users, ...others];
  }, [notifyTargetOptions]);

  const items: RingilDropdownItem[] = useMemo(
    () =>
      orderedTargets.map(target => ({
        id: composeId(target),
        label: target.name,
        icon: getIconByType(target.notifyTargetType),
        subLabel:
          target.notifyTargetType === NotifyTargetTypeEnum.DEPARTMENT
            ? tr('NotifyDropdown.numberOfMembers', 'members: {{count}}', { count: target.userCount })
            : undefined,
      })),
    [orderedTargets, tr],
  );

  const selectedIds = useMemo(() => values.map(composeId), [values]);

  return (
    <RingilDropdown
      items={items}
      selectedIds={selectedIds}
      onSelectionChange={ids => {
        const next = orderedTargets.filter(t => ids.includes(composeId(t)));
        onSelect(next);
      }}
      readonly={readonly}
      multiSelect={multiSelect}
      selectAllEnabled={selectAllEnabled}
      dataTestIdBase={dataTestIdBase}
      placeholder={tr('NotifyDropdown.inputPlaceholder', 'Add user or department...')}
    />
  );
};

export default NotifyDropdown;